
/**
 * TriageProjectTable Component - Sortable table view for triage projects - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Displays triage project data in a paginated, sortable table format with modal details.
 * Features intelligent sorting (active projects first), pagination controls, and status indicators.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of triage project data objects
 * @param {number|string} [props.pageSize=10] - Number of items per page or 'All' for no pagination
 * @returns {JSX.Element} The TriageProjectTable component
 * 
 * @example
 * <TriageProjectTable 
 *   data={triageProjects} 
 *   pageSize={25} 
 * />
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  useTable,
  tableFeatures,
  rowSortingFeature,
  columnVisibilityFeature,
  createSortedRowModel,
  sortFn_alphanumeric,
  sortFn_text,
  flexRender,
} from '@tanstack/react-table';
import TriageProjectModal from '../../modals/TriageProjectModal';
import { truncateText, removeTime } from '../../utils/textUtils';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSortUp, faSortDown, faSort, faRocket, faMoon, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import './TriageProjectPipeline.css';

// Declared once at module scope so the table is not rebuilt on every render.
// v9 no longer bundles the built-in sort functions: auto-detected columns look
// their sort function up by name and silently fall back to a case-sensitive
// basic compare if it is not registered here.
const features = tableFeatures({
    rowSortingFeature,
    columnVisibilityFeature,
    sortedRowModel: createSortedRowModel(),
    sortFns: {
        alphanumeric: sortFn_alphanumeric,
        text: sortFn_text,
    },
});

const TriageProjectTable = ({ data, pageSize: initialPageSize = 10 }) => {
    const [activeModals, setActiveModals] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(initialPageSize);

    /**
     * Opens a modal to display project details
     * Handles modal stacking and z-index management for multiple open modals
     * 
     * @param {Object} itemData - Project data object to display in modal
     * @param {string|number} itemData.id - Unique identifier for the project
     */
    const handleShowModal = (itemData) => {
        const existingModalIndex = activeModals.findIndex(modal => modal.id === itemData.id);
            if (existingModalIndex !== -1) {
            const updatedModals = [...activeModals];
            const existingModal = updatedModals[existingModalIndex];

            existingModal.zIndex = activeModals.length + 1;
            existingModal.isOpen = true;

            updatedModals.splice(existingModalIndex, 1);
            updatedModals.push(existingModal);

            setActiveModals(updatedModals);
            } else {
            const newModal = {
                id: itemData.id,
                data: itemData,
                isOpen: true,
                top: `${15 + (activeModals.length * 2)}%`,
                left: `${30 + (activeModals.length * 2)}%`,
                zIndex: activeModals.length + 1,
            };
            setActiveModals(prev => [...prev, newModal]);
        }
    };

    /**
     * Closes a modal by its ID and removes it from active modals
     * 
     * @param {string|number} id - Unique identifier for the modal to close
     */
    const handleCloseModal = (id) => {
        setActiveModals(prev =>
            prev.map(modal =>
                modal.id === id ? { ...modal, isOpen: false } : modal
            )
        );
        setActiveModals(prev => prev.filter(modal => modal.id !== id));
    };

    /**
     * Generates status display component with appropriate icon and styling
     * 
     * @param {string} status - Project status string
     * @returns {JSX.Element} Formatted status display with icon
     */
    const getStatusDisplay = (status) => {
        if (!status) return 'Unknown Status';
        
        const statusLower = status.toLowerCase();
        switch (statusLower) {
            case 'completed':
            case 'complete':
                return (
                    <span className="status-display status-complete">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        Complete
                    </span>
                );
            case 'in progress':
            case 'active':
            case 'live':
                return (
                    <span className="status-display status-active">
                        <FontAwesomeIcon icon={faRocket} />
                        {status}
                    </span>
                );
            case 'inactive':
                return (
                    <span className="status-display status-default">
                        <FontAwesomeIcon icon={faMoon} />
                        {status}
                    </span>
                );
            default:
                return (
                    <span className="status-display status-default">
                        <FontAwesomeIcon icon={faRocket} />
                        {status}
                    </span>
                );
        }
    };

    // Pagination calculations
    const paginationInfo = useMemo(() => {
        // Sort data: Live/Active at the top, then most recent dates
        const sortedData = [...data].sort((a, b) => {
            const aStatus = a.status ? a.status.toLowerCase() : '';
            const bStatus = b.status ? b.status.toLowerCase() : '';
            
            if ((aStatus === 'live' || aStatus === 'active') && (bStatus !== 'live' && bStatus !== 'active')) return -1;
            if ((bStatus === 'live' || bStatus === 'active') && (aStatus !== 'live' && aStatus !== 'active')) return 1;
            
            const aDate = a.dateItemAddedtoTriage ? new Date(a.dateItemAddedtoTriage) : new Date(0);
            const bDate = b.dateItemAddedtoTriage ? new Date(b.dateItemAddedtoTriage) : new Date(0);
            return bDate - aDate;
        });

        const itemsPerPage = pageSize === 'All' ? sortedData.length : parseInt(pageSize);
        const totalPages = pageSize === 'All' ? 1 : Math.ceil(sortedData.length / itemsPerPage);
        const startIndex = currentPage * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const pageData = sortedData.slice(startIndex, endIndex);
        
        return {
            totalItems: sortedData.length,
            totalPages,
            currentPageData: pageData,
            startIndex,
            endIndex
        };
    }, [data, currentPage, pageSize]);

    // Current page data for display
    const visibleData = paginationInfo.currentPageData;
    const totalItems = paginationInfo.totalItems;
    const totalPages = paginationInfo.totalPages;

    // Reset to first page when pageSize changes
    useEffect(() => {
        setCurrentPage(0);
    }, [pageSize, data.length]);

    const canPreviousPage = currentPage > 0;
    const canNextPage = currentPage < totalPages - 1;

    /**
     * Navigates to the previous page if available
     */
    const previousPage = () => {
        if (canPreviousPage) {
            setCurrentPage(currentPage - 1);
        }
    };

    /**
     * Navigates to the next page if available
     */
    const nextPage = () => {
        if (canNextPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    /**
     * Updates page size and resets to first page
     * 
     * @param {number|string} newPageSize - New page size value
     */
    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
        setCurrentPage(0);
    };

    // React-table columns definition
    const columns = React.useMemo(
        () => [
            {
                header: 'Name',
                accessorKey: 'name',
                cell: ({ cell }) => (
                    <Tippy content={cell.getValue() || 'N/A'}>
                        <div className="table-cell-content">
                            {truncateText(cell.getValue() || 'N/A', 50)}
                        </div>
                    </Tippy>
                )
            },
            {
                header: 'Description',
                accessorKey: 'childName',
                cell: ({ cell }) => (
                    <Tippy content={cell.getValue() || 'N/A'}>
                        <div className="table-cell-content">
                            {truncateText(cell.getValue() || 'N/A', 70)}
                        </div>
                    </Tippy>
                )
            },
            {
                header: 'Status',
                accessorKey: 'status',
                cell: ({ cell }) => (
                    <Tippy content={`Status: ${cell.getValue() || 'Unknown'}`}>
                        <div className="table-cell-content">
                            {getStatusDisplay(cell.getValue())}
                        </div>
                    </Tippy>
                )
            },
            {
                header: 'Start Date',
                accessorKey: 'dateItemAddedtoTriage',
                cell: ({ cell }) => (
                    <Tippy content={cell.getValue() || 'No start date'}>
                        <div className="table-cell-content">
                            {cell.getValue() || 'No date'}
                        </div>
                    </Tippy>
                )
            },
            {
                header: 'End Date',
                accessorKey: 'closeDate',
                cell: ({ cell }) => (
                    <Tippy content={cell.getValue() ? removeTime(cell.getValue()) : 'No end date'}>
                        <div className="table-cell-content">
                            {cell.getValue() ? removeTime(cell.getValue()) : 'No date'}
                        </div>
                    </Tippy>
                )
            }
        ],
        []
    );

    // React-table hooks
     const table = useTable({
        features,
        data: paginationInfo.currentPageData,
        columns,
    });

    return (
        <div className="triage-project-table-container">
            {/* Table */}
            <div className="triage-project-table-wrapper">
                {totalItems === 0 ? (
                    <div className="no-results">
                        <div className="no-results-content">
                            <i className="fas fa-search"></i>
                            <h3>No projects found</h3>
                            <p>Try adjusting your search criteria or filters to see more results.</p>
                        </div>
                    </div>
                ) : (
                    <div className="triage-project-table-responsive">
                        <table className="triage-project-table">
                            <thead>
                                {table.getHeaderGroups().map(headerGroup => (
                                        <tr key={headerGroup.id}>
                                            {headerGroup.headers.map(header => {
                                                const isSorted = header.column.getIsSorted();
                                                const canSort = header.column.getCanSort();
                                                return (
                                                    <th 
                                                        key={header.id}
                                                        className={`col-${header.id} ${isSorted ? 'sorted' : ''}`}
                                                        data-sortable={canSort}
                                                        data-sorted={!!isSorted}
                                                        aria-sort={
                                                            isSorted === 'asc'
                                                            ? 'ascending'
                                                            : isSorted === 'desc'
                                                            ? 'descending'
                                                            : 'none'
                                                        }
                                                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                                                        style={{ cursor: canSort ? 'pointer' : 'default' }}
                                                    >
                                                        <div className="header-content">
                                                            <span className="header-text">
                                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                            </span>
                                                            {canSort ? (
                                                                <span className={`sort-icon ${isSorted ? 'active' : ''}`}>
                                                                    {isSorted === 'desc' ? (
                                                                    <FontAwesomeIcon icon={faSortDown} title="Sorted Descending" />
                                                                    ) : isSorted === 'asc' ? (
                                                                    <FontAwesomeIcon icon={faSortUp} title="Sorted Ascending" />
                                                                    ) : (
                                                                    <FontAwesomeIcon icon={faSort} title="Click to Sort" />
                                                                    )}
                                                                </span>
                                                            ) : (
                                                                <span className="sort-icon disabled" style={{ opacity: 0.3 }}>
                                                                    <FontAwesomeIcon icon={faGripVertical} title="Not Sortable" />
                                                                </span>
                                                            )}
                                                        </div>
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                ))}
                            </thead>
                            <tbody>
                                {table.getRowModel().rows.map(row => (
                                    <tr
                                        key={row.id} 
                                        className="triage-project-table-row" 
                                        onClick={() => handleShowModal(row.original)}
                                    >
                                        {row.getVisibleCells().map(cell => (
                                            <td key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination Controls */}
            {pageSize !== 'All' && totalPages > 1 && (
                <div className="pagination">
                    <div className="pagination-info">
                        Page {currentPage + 1} of {totalPages}
                    </div>
                    <div className="pagination-controls">
                        <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(0)}
                            disabled={!canPreviousPage}
                        >
                            <i className="fas fa-angle-double-left"></i>
                        </button>
                        <button 
                            className="pagination-btn"
                            onClick={previousPage}
                            disabled={!canPreviousPage}
                        >
                            <i className="fas fa-angle-left"></i>
                        </button>
                        
                        {/* Page numbers */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 2) {
                                pageNum = i + 1;
                            } else if (currentPage >= totalPages - 3) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 1 + i;
                            }
                            
                            return (
                                <button
                                    key={pageNum}
                                    className={`pagination-btn ${pageNum === currentPage + 1 ? 'active' : ''}`}
                                    onClick={() => setCurrentPage(pageNum - 1)}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                        
                        <button 
                            className="pagination-btn"
                            onClick={nextPage}
                            disabled={!canNextPage}
                        >
                            <i className="fas fa-angle-right"></i>
                        </button>
                        <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(totalPages - 1)}
                            disabled={!canNextPage}
                        >
                            <i className="fas fa-angle-double-right"></i>
                        </button>
                    </div>
                </div>
            )}

            {activeModals.map((modal) => (
                modal.isOpen && (
                <TriageProjectModal
                    key={modal.id}
                    data={modal.data}
                    show={modal.isOpen}
                    onClose={() => handleCloseModal(modal.id)}
                    allowMove={true}
                        style={{
                            top: modal.top,
                            left: modal.left,
                            zIndex: modal.zIndex
                        }}
                    />
                )
            ))}
        </div>
    );
};

export default TriageProjectTable;
